/**
 * useEntityProfilePhoto — read + set a VESSEL's PROFILE image. FT-436
 * (2026-08-08). VESSELS-ONLY SLICE (the `entityId` is always a vessel id).
 *
 * ── REUSE, don't reinvent ────────────────────────────────────────────────────
 * The profile image rides the EXISTING record-photo pipeline unchanged:
 *   - Storage: Dexie `photos` / `photoThumbs` via `usePhotoStore(entityId)`.
 *     We pass the vessel id as the store's `vesselId` scope — the store
 *     partitions purely by that id string, and record-evidence photos (keyed by
 *     the same vesselId + their own recordType) are untouched.
 *   - Keying: recordType = PROFILE_RECORD_TYPE ('entity_profile'),
 *     recordId = entityId, photoId = profilePhotoId(entityId) (DETERMINISTIC —
 *     one profile per vessel; re-set OVERWRITES via put + a CREATE carrying a
 *     newer updatedAt, which the photoPullMerge staleness guard replaces on
 *     other devices — no orphan accretion, no delete/tombstone race).
 *   - Sync: `usePhotoStore.savePhoto` already enqueues `sr.photos.v1` to the
 *     FT-234 outbox. The current vessel's profile photo pulls back automatically;
 *     OTHER assigned vessels' profile photos hydrate cross-device via the
 *     additive read-only fleet pull (src/sync/fleetProfilePhotoPull.js).
 *     ADDITIVE — record photos + FT-234 outbox are NOT touched.
 *   - Compression (HARD REQ): every image (uploaded OR placeholder) is routed
 *     through src/utils/imageCompression.js (FT-577: compressImageWithinBudget,
 *     which also enforces the real upload-body budget) BEFORE it reaches the store.
 *
 * ── PERMISSION ───────────────────────────────────────────────────────────────
 * This hook does NOT self-gate the WRITE beyond a defensive guard — the UI
 * (SetImageModal / card affordance) is the permission surface (see canSetImage).
 * Read is always allowed (a photo set by an admin DISPLAYS for crew).
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { usePhotoStore } from '../PhotoAttachment/hooks/usePhotoStore';
import { compressImageWithinBudget } from '../../../utils/imageCompression';
import { computePhotoPushBodyBytes } from '../../../utils/photoPayloadBudget';
import {
  PROFILE_RECORD_TYPE,
  profilePhotoId,
  dataUrlToBlob,
  getPlaceholder,
} from './entityProfilePhoto';

// Compression budgets for an entity card image. The card photo area spans a
// whole card (~324px wide × h-32), so the FULL image is stored at a HIGH
// resolution (long edge ~1600px) and rendered directly on the card — sharp on
// retina, yet JPEG-compressed to a modest sync payload. The thumbnail is a
// small preview only (SetImageModal etc.), NOT what the card shows.
//
// NB: PROFILE_MAX_KB is a SOFT budget — compression holds resolution and trades
// quality (down to ~0.5) toward it, so the card stays sharp even if slightly
// over. FT-577: on top of that there is now a HARD budget on the real upload
// body (utils/photoPayloadBudget); if the soft pass can't get under it, the
// image IS shrunk, and if nothing fits the user gets a visible error instead of
// a silently-failing upload. This is a PROFILE-ONLY path; the shared 200px
// record-photo default (crew photos / record evidence) is untouched.
const PROFILE_MAX_KB = 400;
const PROFILE_MAX_DIMENSION = 1600; // long-edge target for the stored full image
const PROFILE_START_QUALITY = 0.8;
const PROFILE_THUMB_PX = 512;

/**
 * @param {string} entityId  the vessel id — REQUIRED to read/write.
 * @param {string} [capturedBy] user id stamped on the photo row (audit).
 * @returns {{
 *   photoUrl: string|null,   // object-URL for the FULL compressed profile image
 *                            //   (what the fleet CARD renders — sharp, not the thumb)
 *   thumbUrl: string|null,   // object-URL for the small square thumb (tiny previews)
 *   loading: boolean,
 *   saving: boolean,
 *   hasPhoto: boolean,
 *   setUploadedImage: (file: File) => Promise<void>,
 *   setPlaceholderImage: (placeholderKey: string) => Promise<void>,
 *   refresh: () => void,
 * }}
 */
export function useEntityProfilePhoto(entityId, capturedBy = 'unknown') {
  const store = usePhotoStore(entityId);
  const [photoUrl, setPhotoUrl] = useState(null);
  const [thumbUrl, setThumbUrl] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [hasPhoto, setHasPhoto] = useState(false);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);

  const load = useCallback(async () => {
    if (!entityId) {
      setPhotoUrl(null);
      setThumbUrl(null);
      setHasPhoto(false);
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const id = profilePhotoId(entityId);
      // FT-436: the CARD renders the FULL compressed image (sharp), not the
      // thumbnail. Fetch both — full for display, thumb for tiny previews. Fall
      // back to the thumb URL if the full blob is somehow missing.
      const [full, thumb] = await Promise.all([
        store.getFullUrl(id),
        store.getThumb(id),
      ]);
      if (!mountedRef.current) return;
      const displayUrl = full?.url || thumb?.url || null;
      setPhotoUrl(displayUrl);
      setThumbUrl(thumb?.url || null);
      setHasPhoto(!!displayUrl);
    } catch (_) {
      if (mountedRef.current) { setPhotoUrl(null); setThumbUrl(null); setHasPhoto(false); }
    } finally {
      if (mountedRef.current) setLoading(false);
    }
  }, [entityId, store]);

  // Initial load + reload when a cross-device pull hydrates photos for this
  // entity (photoPullMerge dispatches sr:photos:hydrated after a merge).
  useEffect(() => {
    load();
    const onHydrated = (evt) => {
      // vesselId on the event is the pulled vessel partition (our entityId when
      // that vessel is current, or another assigned vessel via the fleet pull).
      const pulled = evt?.detail?.vesselId;
      if (!pulled || pulled === entityId) load();
    };
    window.addEventListener('sr:photos:hydrated', onHydrated);
    window.addEventListener('sr:photos:deleted', onHydrated);
    return () => {
      window.removeEventListener('sr:photos:hydrated', onHydrated);
      window.removeEventListener('sr:photos:deleted', onHydrated);
    };
  }, [entityId, load]);

  /**
   * Core save: given full + thumb data-URLs (already compressed), persist +
   * enqueue via the shared photo store. Deterministic id → overwrite.
   */
  const saveFromDataUrls = useCallback(async (fullDataUrl, thumbDataUrl) => {
    if (!entityId) throw new Error('[useEntityProfilePhoto] entityId is required');
    const [fullBlob, thumbBlob] = await Promise.all([
      dataUrlToBlob(fullDataUrl),
      dataUrlToBlob(thumbDataUrl),
    ]);
    await store.savePhoto(
      {
        photoId: profilePhotoId(entityId),
        recordType: PROFILE_RECORD_TYPE,
        recordId: entityId,
        blob: fullBlob,
        mime: fullBlob.type || 'image/jpeg',
        bytes: fullBlob.size,
        capturedAt: Date.now(),
        capturedBy: capturedBy || 'unknown',
      },
      thumbBlob,
    );
  }, [entityId, capturedBy, store]);

  /**
   * FT-577: compress full image + thumbnail so the REAL upload body fits the
   * hard budget (quality AND dimensions reduced if needed). Rejects with
   * PhotoTooLargeError (plain-English; SetImageModal shows it) if it can't —
   * never saves an image that would then silently fail to upload.
   */
  const compressForUpload = useCallback((file) => {
    const photoId = profilePhotoId(entityId);
    return compressImageWithinBudget(file, {
      maxDimension: PROFILE_MAX_DIMENSION,
      startQuality: PROFILE_START_QUALITY,
      softTargetKB: PROFILE_MAX_KB,
      thumbSize: PROFILE_THUMB_PX,
      measure: (full, thumb) => computePhotoPushBodyBytes({
        // Same fields saveFromDataUrls() stores (capturedAt is a 13-digit ms
        // timestamp either way, so the byte count matches).
        photoData: {
          photoId,
          recordType: PROFILE_RECORD_TYPE,
          recordId: entityId,
          mime: full.mime,
          bytes: full.bytes,
          capturedAt: Date.now(),
          capturedBy: capturedBy || 'unknown',
        },
        fullBytes: full.bytes,
        fullMime: full.mime,
        thumbBytes: thumb.bytes,
        thumbMime: thumb.mime,
        vesselId: entityId,
      }),
    });
  }, [entityId, capturedBy]);

  /**
   * Upload path: compress a picked File through imageCompression (HARD REQ),
   * then save. Compression is ALWAYS applied — the store never sees the raw file.
   */
  const setUploadedImage = useCallback(async (file) => {
    if (!file) return;
    setSaving(true);
    try {
      // high-res (NOT the 200px record-photo default), FT-577 budget-enforced
      const { fullDataUrl, thumbDataUrl } = await compressForUpload(file);
      await saveFromDataUrls(fullDataUrl, thumbDataUrl);
      await load();
    } finally {
      if (mountedRef.current) setSaving(false);
    }
  }, [compressForUpload, saveFromDataUrls, load]);

  /**
   * Placeholder path: render the built-in SVG placeholder → File → SAME
   * imageCompression pass → save. Routing it through compression (rather than
   * storing the SVG verbatim) keeps ONE code path, guarantees a raster thumb,
   * and means a picked placeholder syncs + displays for crew exactly like an
   * uploaded photo.
   */
  const setPlaceholderImage = useCallback(async (placeholderKey) => {
    const placeholder = getPlaceholder(placeholderKey);
    if (!placeholder) throw new Error(`[useEntityProfilePhoto] unknown placeholder: ${placeholderKey}`);
    setSaving(true);
    try {
      const svgBlob = await dataUrlToBlob(placeholder.dataUrl);
      // Wrap as a File so compressImage's FileReader path is identical to upload.
      const file = new File([svgBlob], `${placeholder.key}.svg`, { type: svgBlob.type || 'image/svg+xml' });
      const { fullDataUrl, thumbDataUrl } = await compressForUpload(file);
      await saveFromDataUrls(fullDataUrl, thumbDataUrl);
      await load();
    } finally {
      if (mountedRef.current) setSaving(false);
    }
  }, [compressForUpload, saveFromDataUrls, load]);

  return {
    photoUrl,
    thumbUrl,
    loading,
    saving,
    hasPhoto,
    setUploadedImage,
    setPlaceholderImage,
    refresh: load,
  };
}

export default useEntityProfilePhoto;
